import { useState } from 'react';
import { Monitor, XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useSeatHistory } from '@/services/queries';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { StatusPill } from '@/shared/ui/status-pill';
import { cn } from '@/lib/utils';
import type { CustomerPortalUser, CustomerSeat, SeatDevice, SeatEvent } from '@/types';
import { EVENT_STYLE } from '../audit/event-style';
import { roleLabel } from './credentials';

/**
 * A device's status in the words the seat table uses.
 *
 * `revoked` on a device is what releasing its seat leaves behind - the row is
 * kept so the machine's history survives - so it reads "Released", and inert
 * rather than red: nothing is wrong, the seat simply moved on.
 */
const DEVICE_LABEL: Record<SeatDevice['status'], { pill: string; label: string }> = {
  active: { pill: 'active', label: 'Active' },
  soft_grace: { pill: 'grace', label: 'Grace' },
  hard_blocked: { pill: 'hard_blocked', label: 'Blocked' },
  revoked: { pill: 'released', label: 'Released' },
};

/** What "Activations" narrows the timeline to: events about a machine
 *  binding to the seat, losing it, or being challenged on it. */
const ACTIVATION_EVENTS = new Set<SeatEvent['eventType']>([
  'activated',
  'released',
  'fingerprint_mismatch_soft',
  'fingerprint_mismatch_hard',
  'revoked_by_admin',
  'revoked_by_server',
]);

const EVENTS_PAGE = 25;
/** The endpoint's cap. Past it the Action Log is the place to look. */
const EVENTS_MAX = 100;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function SectionTitle({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <div className="mb-2.5 flex items-center justify-between gap-3">
      <h3 className="text-[11px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
        {children}
      </h3>
      {aside}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate text-[12.5px]">{children}</dd>
    </div>
  );
}

export interface SeatDrawerTarget {
  seat: CustomerSeat;
  holder: CustomerPortalUser | undefined;
  licenceLabel: string;
  /** The seat-table's own words for it, so the drawer cannot disagree. */
  seatState: string;
  machineState: string;
}

/**
 * The side drawer a seat row on the customer page opens: who holds the seat,
 * the machines that have held it, and the licence events about this one key.
 *
 * Read-only on purpose. Reset, revoke and disable are the row's own buttons,
 * one click away behind the drawer; a second set here would be a second place
 * for their confirmations to drift apart.
 *
 * `target` stays set while `open` goes false, so the content is still there
 * for the slide-out rather than blanking first.
 */
export function SeatDrawer({
  organizationId,
  target,
  open,
  onOpenChange,
}: {
  organizationId: string;
  target: SeatDrawerTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-overlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-140 flex-col border-l border-border bg-background shadow-dialog outline-none data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=closed]:duration-200 data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=open]:duration-300"
          // Focus would otherwise land on the first filter button and draw a
          // ring round it every time the drawer opens.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
        >
          {target && (
            <DrawerBody key={target.seat.id} organizationId={organizationId} target={target} />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function DrawerBody({
  organizationId,
  target,
}: {
  organizationId: string;
  target: SeatDrawerTarget;
}) {
  const { seat, holder } = target;
  const [limit, setLimit] = useState(EVENTS_PAGE);
  const history = useSeatHistory(organizationId, seat.id, limit);
  const [onlyActivations, setOnlyActivations] = useState(false);

  const devices = history.data?.devices ?? [];
  const events = (history.data?.events ?? []).filter(
    (event) => !onlyActivations || ACTIVATION_EVENTS.has(event.eventType),
  );

  return (
    <>
      <header className="flex items-start gap-3 border-b border-border px-6 py-5">
        <div className="min-w-0 flex-1">
          <DialogPrimitive.Title className="truncate text-lg leading-tight font-semibold">
            {holder?.fullName ?? seat.email}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-1 truncate font-mono text-[12px] text-muted-foreground">
            {seat.email}
          </DialogPrimitive.Description>
        </div>
        <DialogPrimitive.Close className="mt-1 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:outline-hidden">
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </header>

      <div className="flex-1 space-y-7 overflow-y-auto px-6 py-5">
        {history.isError && (
          <p className="rounded-lg border border-pill-negative bg-pill-negative px-3 py-2 text-[12.5px] text-pill-negative-foreground">
            {history.error.message}
          </p>
        )}

        <section>
          <SectionTitle>Licence</SectionTitle>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-border bg-card px-4 py-3.5 sm:grid-cols-3">
            <Field label="Seat type">{target.licenceLabel}</Field>
            <Field label="Status">
              <StatusPill status={target.seatState} minWidth={84} />
            </Field>
            <Field label="Machine">
              <StatusPill status={target.machineState} minWidth={76} />
            </Field>
            <div className="col-span-2 min-w-0 sm:col-span-3">
              <dt className="text-[11px] text-muted-foreground">Licence key</dt>
              <dd className="mt-0.5 font-mono text-[12.5px] break-all">
                {/* Same rule as the seat table: a revoked seat's key is dead,
                    and showing it invites somebody to read it down the phone. */}
                {seat.status === 'revoked' ? '—' : seat.licenseKey}
              </dd>
            </div>
            <Field label="Activated">
              <span className="font-mono">{formatDate(seat.activatedAt)}</span>
            </Field>
            <Field label="Expires">
              <span className="font-mono">{formatDate(seat.expiresAt)}</span>
            </Field>
            <Field label="Validity">
              <span className="font-mono">{seat.validityDays} days</span>
            </Field>
            {holder && (
              <>
                <Field label="Role">{roleLabel(holder.role)}</Field>
                <Field label="Username">
                  <span className="font-mono">{holder.username}</span>
                </Field>
                <Field label="Login">
                  <StatusPill status={holder.status === 'disabled' ? 'blocked' : holder.status}>
                    {holder.status === 'disabled' ? 'Disabled' : undefined}
                  </StatusPill>
                </Field>
              </>
            )}
          </dl>
        </section>

        <section>
          <SectionTitle>Devices</SectionTitle>
          {history.isPending ? (
            <Skeleton className="h-16 w-full" />
          ) : devices.length === 0 ? (
            <p className="text-[12.5px] text-muted-foreground">
              No computer has activated this key yet.
            </p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
              {devices.map((device) => {
                const state = DEVICE_LABEL[device.status];
                return (
                  <li
                    key={device.id}
                    className={cn(
                      'flex items-center gap-3 px-3.5 py-2.5',
                      device.status === 'revoked' && 'opacity-60',
                    )}
                  >
                    <Monitor
                      className="size-4 flex-none text-muted-foreground"
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-[11.5px]">
                        NμDent {device.nudentVersion ?? '—'} · last heartbeat{' '}
                        {device.lastHeartbeatAt ? formatDateTime(device.lastHeartbeatAt) : 'never'}
                      </p>
                      <p className="truncate font-mono text-[11px] text-muted-foreground">
                        First activated {formatDate(device.firstActivatedAt)} · Last seen{' '}
                        {formatDateTime(device.lastSeenAt)}
                        {device.status === 'soft_grace' && device.graceExpiresAt
                          ? ` · Grace ends ${formatDate(device.graceExpiresAt)}`
                          : ''}
                      </p>
                    </div>
                    <StatusPill status={state.pill} minWidth={76}>
                      {state.label}
                    </StatusPill>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section>
          <SectionTitle
            aside={
              <div
                role="group"
                aria-label="Timeline filter"
                className="inline-flex rounded-md bg-muted p-0.5 text-[11.5px] font-medium"
              >
                {[
                  { value: false, label: 'All events' },
                  { value: true, label: 'Activations' },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    aria-pressed={onlyActivations === option.value}
                    onClick={() => {
                      setOnlyActivations(option.value);
                    }}
                    className={cn(
                      'rounded-[5px] px-2.5 py-1 transition-colors',
                      onlyActivations === option.value
                        ? 'bg-card text-foreground shadow-xs'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            }
          >
            Event timeline
          </SectionTitle>

          {history.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : events.length === 0 ? (
            <p className="text-[12.5px] text-muted-foreground">
              {onlyActivations ? 'No activation events yet.' : 'Nothing has happened yet.'}
            </p>
          ) : (
            <ol className="relative space-y-4 before:absolute before:top-2 before:bottom-2 before:left-3 before:w-px before:bg-border">
              {events.map((event) => (
                <TimelineItem key={event.id} event={event} />
              ))}
            </ol>
          )}

          {history.data?.hasMoreEvents &&
            (limit < EVENTS_MAX ? (
              <Button
                variant="outline"
                size="sm"
                className="mt-4 w-full"
                disabled={history.isFetching}
                onClick={() => {
                  setLimit((current) => Math.min(current + EVENTS_PAGE, EVENTS_MAX));
                }}
              >
                {history.isFetching ? 'Loading…' : 'Show older events'}
              </Button>
            ) : (
              <p className="mt-4 text-center text-[11.5px] text-muted-foreground">
                Showing the latest {EVENTS_MAX} of {history.data.eventsTotal}. The Action Log has
                the rest.
              </p>
            ))}
        </section>
      </div>
    </>
  );
}

function TimelineItem({ event }: { event: SeatEvent }) {
  const style = EVENT_STYLE[event.eventType];
  const Icon = style.icon;
  return (
    <li className="relative flex gap-3">
      <span
        className="relative z-10 flex size-6 flex-none items-center justify-center rounded-[7px] ring-4 ring-background"
        style={{ background: style.bg, color: style.fg }}
      >
        <Icon size={13} strokeWidth={1.9} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] leading-snug">{event.description}</p>
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
          {event.label} · {event.actorName ?? 'System'} · {formatDateTime(event.createdAt)}
        </p>
      </div>
    </li>
  );
}
